import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const slateEdge: TemplateRegistration = {
  meta: {
    id: 'slate-edge',
    name: 'Slate Edge',
    category: 'Minimal',
    description: 'Cool grey slate with crisp edges and three-column grid',
    previewImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Geist', 'Atkinson Hyperlegible']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="about-bleed">
        <div class="about-overlay-panel">
          <h2 class="section-title">About Us</h2>
          <div class="slate-divider"></div>
          <p class="about-text">${safeAbout}</p>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceRows = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-row">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <div class="service-text">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="slate-divider"></div>
          <div class="services-list">${serviceRows}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-light">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="slate-divider"></div>
          <div class="grid-3">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const featured = data.testimonials[0];
      const tName = sanitizeText(featured.name);
      const tContent = sanitizeText(featured.content);
      const tRole = featured.role ? sanitizeText(featured.role) : '';
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="slate-divider"></div>
          <div class="testimonial-featured">
            <p class="featured-quote">&ldquo;${tContent}&rdquo;</p>
            <div class="featured-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${featured.rating ? `<div class="testimonial-stars">${renderStars(featured.rating)}</div>` : ''}
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-light">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="slate-divider"></div>
          <div class="grid-3">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-card">
            <h3>${pName}</h3>
            <p>${pDesc}</p>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="slate-divider"></div>
          <div class="grid-3">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-light">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <div class="slate-divider"></div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="slate-divider"></div>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #1E293B; --bg: #F1F5F9; --bg-alt: #E2E8F0; --accent: #64748B;
      --text: #0F172A; --text-muted: #64748B;
      --font-heading: 'Geist', sans-serif; --font-body: 'Atkinson Hyperlegible', sans-serif;
      --shadow-sm: 0 1px 3px rgba(15,23,42,.06);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.65; }
    .container { max-width: 1140px; margin: 0 auto; padding: 0 28px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .75; }

    /* SLATE DIVIDER */
    .slate-divider { width: 60px; height: 3px; background: var(--accent); margin-bottom: 36px; }

    /* GRID-3 */
    .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(241,245,249,.96); backdrop-filter: blur(10px); border-bottom: 2px solid var(--primary); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 28px; max-width: 1140px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.2rem; color: var(--text); font-weight: 700; letter-spacing: -0.5px; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .82rem; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; }
    .nav-links a:hover { color: var(--primary); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--primary); margin: 5px 0; transition: .3s; }

    /* HERO - reverse split (image LEFT, text RIGHT) */
    .hero { position: relative; min-height: 100vh; display: flex; overflow: hidden; background: linear-gradient(180deg, var(--bg-alt), var(--bg)); }
    .hero-split { display: grid; grid-template-columns: 1fr 1fr; min-height: 100vh; width: 100%; }
    .hero-image-side { position: relative; overflow: hidden; }
    .hero-image-side img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .hero-image-side .hero-no-img { width: 100%; height: 100%; background: var(--bg-alt); }
    .hero-text-side { display: flex; align-items: center; padding: 120px 48px 80px; }
    .hero-content { max-width: 500px; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.4rem, 5.5vw, 4rem); line-height: 1.12; margin-bottom: 20px; font-weight: 800; color: var(--text); letter-spacing: -1px; }
    .hero-sub { font-size: 1.05rem; color: var(--text-muted); max-width: 540px; margin-bottom: 40px; line-height: 1.75; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--primary); color: var(--bg); font-family: var(--font-heading); font-size: .85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(30,41,59,.2); opacity: 1; color: var(--bg); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-light { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.4rem, 3vw, 2rem); font-weight: 800; margin-bottom: 12px; color: var(--text); letter-spacing: -0.5px; text-transform: uppercase; }

    /* ABOUT - full-bleed with overlay panel */
    .about-bleed { position: relative; background: var(--primary); padding: 80px 0; }
    .about-bleed .about-overlay-panel { background: rgba(241,245,249,.95); max-width: 700px; margin: 0 auto; padding: 48px 40px; position: relative; z-index: 1; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); max-width: 800px; line-height: 1.85; }

    /* SERVICES - single-col list with dividers */
    .services-list { display: flex; flex-direction: column; gap: 0; max-width: 800px; }
    .service-row { display: flex; align-items: flex-start; gap: 20px; padding: 28px 0; border-bottom: 2px solid #CBD5E1; transition: background .2s; }
    .service-row:hover { background: rgba(30,41,59,.03); }
    .service-icon { flex-shrink: 0; margin-top: 4px; }
    .icon-char { font-size: 24px; line-height: 1; color: var(--primary); }
    .service-text { flex: 1; }
    .service-text h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 6px; color: var(--text); }
    .service-text p { color: var(--text-muted); font-size: .88rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 10px; font-weight: 700; font-size: 1rem; color: var(--primary); }

    /* GALLERY */
    .gallery-item img { width: 100%; height: 200px; object-fit: cover; display: block; box-shadow: var(--shadow-sm); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.02); }

    /* TESTIMONIALS - single large featured quote */
    .testimonial-featured { max-width: 700px; margin: 0 auto; text-align: center; }
    .testimonial-featured .featured-quote { font-size: 1.4rem; font-style: italic; color: var(--text-muted); line-height: 1.8; margin-bottom: 24px; border-left: 4px solid var(--primary); padding-left: 24px; text-align: left; }
    .testimonial-featured .featured-author strong { display: block; color: var(--text); font-size: 1.05rem; }
    .testimonial-featured .featured-author span { color: var(--text-muted); font-size: .88rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #CBD5E1; font-size: 1rem; }
    .star.filled { color: var(--primary); }

    /* FAQ */
    .faq-item { background: #fff; padding: 24px; border: 1px solid #CBD5E1; }
    .faq-q { font-family: var(--font-heading); font-size: .95rem; font-weight: 700; margin-bottom: 8px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.65; font-size: .9rem; }

    /* PRICE CARD */
    .price-card { background: #fff; padding: 28px 24px; border: 1px solid #CBD5E1; box-shadow: var(--shadow-sm); }
    .price-card h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 6px; color: var(--text); }
    .price-card p { color: var(--text-muted); font-size: .88rem; margin-bottom: 12px; line-height: 1.6; }
    .price-amount { font-family: var(--font-heading); font-size: 1.3rem; color: var(--primary); font-weight: 800; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #CBD5E1; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--primary); padding: 14px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.1rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .9rem; letter-spacing: 0.5px; }

    /* PROMO */
    .promo-banner { background: var(--bg); padding: 18px 0; border-bottom: 2px solid var(--primary); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--primary); color: var(--bg); font-family: var(--font-heading); padding: 6px 18px; font-size: .85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .88rem; }

    /* CONTACT - two-col info + form */
    .contact-grid { display: grid; grid-template-columns: 1fr 1.2fr; gap: 40px; align-items: start; }
    .contact-info { display: flex; flex-direction: column; gap: 18px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 700; text-transform: uppercase; font-size: .78rem; letter-spacing: 1.5px; color: var(--primary); }
    .contact-form { display: flex; flex-direction: column; gap: 14px; }
    .contact-form input, .contact-form textarea { background: #fff; border: 2px solid #CBD5E1; color: var(--text); padding: 13px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); }
    .contact-form button { padding: 14px; background: var(--primary); color: var(--bg); font-family: var(--font-heading); font-size: .85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(30,41,59,.15); }

    /* FOOTER */
    .footer { background: var(--primary); padding: 40px 0; text-align: center; }
    .footer p { color: #94A3B8; font-size: .82rem; letter-spacing: 0.5px; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(241,245,249,.98); border-bottom: 2px solid var(--primary); padding: 16px 28px; gap: 14px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero-split { grid-template-columns: 1fr; }
      .hero-image-side { height: 300px; }
      .contact-grid { grid-template-columns: 1fr; }
    }
    @media (max-width: 480px) {
      .hero-content { padding: 110px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-split">
      <div class="hero-image-side">
        ${heroImage ? `<img src="${escapeAttr(heroImage)}" alt="${escapeAttr(safeName)}">` : `<div class="hero-no-img"></div>`}
      </div>
      <div class="hero-text-side">
        <div class="hero-content">
          <h1>${safeHeadline}</h1>
          <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
          ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Contact Us</a>` : ''}
        </div>
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
